import React, { useContext } from 'react'
import SummaryTable from '@Components/Tables/RoyaltiesTable/SummaryTable'
import { ICONS } from '@Const/common'
import { observer } from 'mobx-react'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/plugin/isSameOrAfter'
import { PayeeRoyalties } from '@Entities/statements/payee-royalties'
import { useFlag } from '@openfeature/react-sdk'
import RoyaltiesLink from '@Components/Navigations/RoyaltiesLink'
import { getUsagePeriod } from '@App/helpers/work-entities/get-usage-period'
import { useOutletContext } from 'react-router'
import { RoyaltiesStatementsContext } from '@Scenes/RightsholderScene/RightsholderRoyaltiesStatements/hooks/useRoyaltiesStatements'

interface RoyaltiesUsagePeriodProps {
    setSelectedData: (selectedDate: PayeeRoyalties) => void
    setSidebarIsOpen: (isOpen: boolean) => void
}

const CUTOFF_DATE: Dayjs = dayjs('2022-06-01')
const CUTOFF_DATE_FOR_PHONO_ADJUSTMENTS: Dayjs = dayjs('2024-09-01')
const CUTOFF_DATE_FOR_ADJUSTMENT_RATE_SHEET_SUMMARY_TABLE: Dayjs = dayjs('2026-05-01')

const RoyaltiesUsagePeriod = (props: RoyaltiesUsagePeriodProps) => {
    const { statementsStore } = useContext<RootStore>(StoresContext)
    const {
        handleNavigateAndSetPeriod,
        statementsStore: { selectedUsagePeriod, distributionSummaryPdfLink, royalties },
    } = useOutletContext<RoyaltiesStatementsContext>()
    const { value: showAdjustmentRateSheet } = useFlag('show-adjustment-rate-sheet', false)
    const { setSelectedData, setSidebarIsOpen } = props
    const usagePeriodDay = statementsStore.selectedUsagePeriod
        ? dayjs(statementsStore.selectedUsagePeriod?.value, 'MM-YYYY')
        : null

    const pdfString = selectedUsagePeriod
        ? `${getUsagePeriod(selectedUsagePeriod).thisUsagePeriod.format('YYYYMM')}.pdf`
        : ''

    return (
        <>
            {selectedUsagePeriod?.value && (
                <>
                    {distributionSummaryPdfLink && (
                        <div className="distribution-summary-pdf-link">
                            <a href={distributionSummaryPdfLink} target="_blank" rel="noreferrer">
                                <img src={ICONS.pdfIcon} alt="distribution-summary-pdf" />
                                Distribution Summary
                            </a>
                        </div>
                    )}
                    {royalties && (
                        <SummaryTable
                            data={royalties}
                            viewStatements={handleNavigateAndSetPeriod}
                            openStatusSidebar={(data) => {
                                setSelectedData(data)
                                setSidebarIsOpen(true)
                            }}
                        />
                    )}
                    <RoyaltiesLink
                        pdfString={pdfString}
                        link="release-notes"
                        linkText="Distribution Notes for Blanket Licensing Activities"
                    />
                    {usagePeriodDay && usagePeriodDay.isSameOrAfter(CUTOFF_DATE_FOR_PHONO_ADJUSTMENTS, 'month') && (
                        <>
                            <RoyaltiesLink
                                pdfString={pdfString}
                                link="release-notes-p3-adjustments"
                                linkText="Distribution Notes for Phono III Adjustments"
                            />
                            <RoyaltiesLink
                                pdfString={pdfString}
                                link="release-notes-p4-adjustments"
                                linkText="Distribution Notes for Phono IV Adjustments"
                            />
                        </>
                    )}
                    {usagePeriodDay && usagePeriodDay.isSameOrAfter(CUTOFF_DATE, 'month') && (
                        <RoyaltiesLink
                            pdfString={pdfString}
                            link="release-notes-historical"
                            linkText="Distribution Notes for Historic Activities"
                        />
                    )}
                    <RoyaltiesLink
                        pdfString={pdfString}
                        link="distribution-certification"
                        linkText="View Distribution Certification"
                    />
                    {showAdjustmentRateSheet &&
                        usagePeriodDay &&
                        usagePeriodDay.isSameOrAfter(CUTOFF_DATE_FOR_ADJUSTMENT_RATE_SHEET_SUMMARY_TABLE, 'month') && (
                            <>
                                <RoyaltiesLink
                                    pdfString={pdfString}
                                    link="adjustment-rate-sheet-summary"
                                    linkText="Adjustment Rate Sheet Summary Table"
                                />
                                <RoyaltiesLink
                                    pdfString={pdfString}
                                    link="adjustment-rate-sheet-details"
                                    linkText="Adjustment Pool Calculation Detail"
                                />
                            </>
                        )}
                </>
            )}
        </>
    )
}

export default observer(RoyaltiesUsagePeriod)
